import { useCallback } from 'react';
import {
  CloseFormShowButton,
  EditFormShowButton,
  SaveFormShowButton,
  useFormShowContext,
  Dropdown,
  TextInput,
} from 'framework/form';
import {
  Filters,
  FiltersLayout,
  EditableListDataGridLayout,
  EDITABLE_LIST_COLLAPSIBLE_ROW,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
} from 'framework/list';
import { Box, Grid2 as Grid, SxProps, Typography, TypographyProps } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getAnchorRowStyle, AnchorField, FormattedTextField } from 'framework/ui/field';
import LinkedMilestonesField from './LinkedMilestonesField';
import NotLinkedToMilestoneField from './NotLinkedToMilestoneField';
import BusinessOutcomesCompleteButton from './BusinessOutcomesCompleteButton';
import OutcomeActiveForField from './OutcomeActiveForField';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  MILESTONE_STATUS,
  OUTCOME_STATUS,
  OUTCOME_STATUS_OPTIONS,
  Milestone,
  useProjectSecurityContext,
  StatusFilter,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';
import { defaultsDeep } from 'lodash';
import { Tooltip } from 'framework/ui/mui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import ListContainsFilter from '../shared/filters/ListContainsFilter';
import { compactDisabledInputSx } from 'domain/projects/newProject/shared/compactDisabledInputSx';

const compactBusinessOutcomeInputSx: SxProps = {
  ...compactDisabledInputSx,
  '.floating-label-input': { mb: 0 },
};

const compactDropdownCellSx: SxProps = {
  '& .floating-label-dropdown': {
    m: '0 !important',
  },
};

type BusinessOutcomesProps = {
  accordionSx?: SxProps;
  titleSx?: SxProps;
  titleVariant?: TypographyProps['variant'];
  gridSx?: SxProps;
  milestones?: { alias: string; title: string; status: string }[];
};

const BusinessOutcomes = ({
  milestones,
  accordionSx,
  titleSx,
  titleVariant = 'h4',
  gridSx = { pl: 2 },
}: BusinessOutcomesProps) => {
  const { isEditMode } = useFormShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { hash } = useLocation();

  const options =
    milestones?.map(({ alias, title }) => ({
      value: alias,
      label: title.length > 0 ? `${alias} – ${title}` : alias,
    })) || [];

  const renderActions = useCallback(() => {
    if (isAccessGranted(ACCESS_INFO_SECTIONS.BUSINESS_OUTCOME, ACCESS_INFO_ACTIONS.MANAGE)) {
      return isEditMode
        ? [<SaveFormShowButton key="save" />, <CloseFormShowButton key="close" />]
        : [<EditFormShowButton key="edit" />];
    }
    return null;
  }, [isAccessGranted, isEditMode]);

  const filters = (
    <Filters>
      <FiltersLayout>
        <StatusFilter label="Status" source="status" data-testid="status-filter" options={OUTCOME_STATUS_OPTIONS} />
        <ListContainsFilter source="milestones" label={'Milestones'} options={options} />
      </FiltersLayout>
    </Filters>
  );

  return (
    <ProjectSectionAccordion
      sx={accordionSx}
      defaultExpanded
      title={
        <Grid
          container
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%', ...gridSx }}
        >
          <Grid size={3}>
            <Typography variant={titleVariant} color="primary" sx={titleSx}>
              Business outcomes
            </Typography>
          </Grid>
          <Grid
            size="auto"
            alignItems="center"
            sx={{
              '.add-filter-button__wrapper': { mb: 0 },
              '.filter-item-wrapper': { mb: 0 },
              '.filters-reset-btn': { mb: 0 },
            }}
          >
            {!isEditMode ? filters : <></>}
          </Grid>
          <Grid size="grow" />
          <Grid size="auto">{renderActions()}</Grid>
        </Grid>
      }
    >
      <EditableListDataGridLayout
        rowProps={data =>
          defaultsDeep(getAnchorRowStyle(data, hash), {
            sx: {
              alignItems: isEditMode ? 'flex-start' : 'center',
              py: isEditMode ? 2 : 0,
            },
          })
        }
        rerenderTableCellsDeps={milestones}
        getRowEnabled={data => data.status !== OUTCOME_STATUS.COMPLETED}
        getTableCells={async isEditModeLayout => {
          if (isEditModeLayout) {
            return Promise.resolve([
              {
                type: EDITABLE_LIST_FIELD,
                source: 'alias',
                isEditor: true,
                component: TextInput,
                componentProps: {
                  label: '',
                  disabled: true,
                },
                gridCellProps: {
                  size: 0.5,
                  sx: compactBusinessOutcomeInputSx,
                },
              },
              {
                type: EDITABLE_LIST_FIELD,
                source: 'name',
                isEditor: true,
                component: TextInput,
                componentProps: { label: 'Business outcome' },
                gridCellProps: { size: 3.5, sx: { '.floating-label-input': { mb: 0 } } },
              },
              {
                type: EDITABLE_LIST_FIELD,
                source: 'description',
                isEditor: true,
                component: TextInput,
                componentProps: { label: 'Description' },
                gridCellProps: { size: 4, sx: { '.floating-label-input': { mb: 0 } } },
              },
              {
                type: EDITABLE_LIST_FIELD,
                source: 'milestones',
                isEditor: true,
                component: Dropdown,
                componentProps: {
                  options,
                  multiple: true,
                  placeholder: 'Select related milestone(s)',
                  label: '',
                },
                gridCellProps: {
                  size: 3.5,
                  sx: compactDropdownCellSx,
                },
              },
            ]);
          }
          return Promise.resolve([
            {
              type: EDITABLE_LIST_COLLAPSIBLE_ROW,
              gridCellProps: { size: 12 },
              title: [
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'alias',
                  isEditor: false,
                  component: AnchorField,
                  gridCellProps: { size: 0.5, justifyContent: 'center', display: 'flex' },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'name',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (str: string) =>
                      str && str.length > 60 ? (
                        <Tooltip title={str}>
                          <div>
                            <Typography
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: 1.2,
                                wordBreak: 'break-word',
                              }}
                            >
                              {str}
                            </Typography>
                          </div>
                        </Tooltip>
                      ) : (
                        str
                      ),
                  },
                  gridCellProps: { size: 3.5 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'milestones',
                  isEditor: false,
                  component: NotLinkedToMilestoneField,
                  componentProps: { color: 'error.dark' },
                  gridCellProps: { size: 2 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'milestones',
                  isEditor: false,
                  component: LinkedMilestonesField,
                  componentProps: {
                    label: '',
                    options: milestones,
                    isCompletedFn: (milestone: Milestone) => milestone?.status === MILESTONE_STATUS.COMPLETED,
                  },
                  gridCellProps: { size: 2.5 },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  component: OutcomeActiveForField,
                  gridCellProps: { size: 2.5, display: 'flex', alignItems: 'center' },
                },
                {
                  type: EDITABLE_LIST_CUSTOM,
                  component: BusinessOutcomesCompleteButton,
                  gridCellProps: {
                    size: 1,
                    justifyContent: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    height: '36px',
                  },
                },
              ],
              details: [
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'description',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    label: '',
                    format: (str: string) => (
                      <Box pb={1} pl={'6px'}>
                        {str ? str : EMPTY_VALUE_INDICATOR}
                      </Box>
                    ),
                  },
                  gridCellProps: { size: 12 },
                },
              ],
            },
          ]);
        }}
      />
    </ProjectSectionAccordion>
  );
};
export default BusinessOutcomes;
